import type { Address } from "viem"
import { useEffect } from "react"
import { reaction } from "mobx"
import { observer } from "mobx-react-lite"
import { useNavigate, useParams } from "react-router-dom"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { CREATE_MANUAL_CL_V2_BREADCRUMBS } from "@/domains/thick-v2/breadcrumbs"
import { CreatePositionCardComposite } from "@/domains/thick-v2/components/create-position/create-position-card-composite"
import { CreatePositionPoolNotFoundView } from "@/domains/thick-v2/components/create-position/create-position-pool-not-found-view"

const CreateManualClPositionPageContent = observer(() => {
  const scaleApp = useScaleApplication()
  const navigate = useNavigate()
  const { address: poolAddressParam } = useParams<{ address: string }>()

  const createPositionStore = scaleApp.ManualClV2.CreatePosition
  const poolAddress = poolAddressParam as Address | undefined

  // Load pool if address is available and reset store when pool address changes
  // Note: useEffect is used here only to trigger the store action, not for data fetching
  // The actual data fetching is handled by OrchestratedQuery in the store
  // The store uses its internal chainId from constructor
  // Note: poolAddress will always be defined because the route requires [address] parameter
  // IMPORTANT: createPositionStore is a singleton and should NOT be in dependency array
  // Including it causes the effect to re-run on every observable change, triggering unnecessary GraphQL queries
  useEffect(() => {
    if (!poolAddress) {
      return
    }

    // Check if we need to load a different pool
    const currentPoolAddress = createPositionStore.pool?.address

    // If pool address changed (or no pool loaded), reset store and load new pool
    // LcAddress.equalsString handles case-insensitive comparison automatically
    if (!currentPoolAddress || !currentPoolAddress.equalsString(poolAddress)) {
      createPositionStore.reset()
      createPositionStore.loadPoolByAddress(poolAddress)
    }

    // Cleanup: reset store when component unmounts
    return () => {
      createPositionStore.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolAddress]) // Only depend on poolAddress - createPositionStore is a stable singleton

  useEffect(() => {
    return reaction(
      () => ({
        tokenId: createPositionStore.createdTokenId,
        poolAddress: createPositionStore.createdPoolAddress ?? poolAddress,
      }),
      ({ tokenId, poolAddress }) => {
        if (!tokenId || !poolAddress) return
        navigate(`/liquidity/cl/v2/${poolAddress}/manage/${tokenId.toString()}`)
      },
      { fireImmediately: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, poolAddress])

  const title = CL_PRODUCT_COPY.v2.createPositionTitle

  return (
    <>
      <MetaData title={title} />
      <PageTitle title={title} items={CREATE_MANUAL_CL_V2_BREADCRUMBS} />

      {createPositionStore.hasPool ? (
        <CreatePositionCardComposite
          store={createPositionStore}
          tickDistributionStore={scaleApp.ManualClV2.TickDistribution}
          poolOhlcStore={scaleApp.ManualClV2.PoolOhlc}
        />
      ) : (
        <CreatePositionPoolNotFoundView />
      )}
    </>
  )
})

export default function CreateManualClPositionPage() {
  return (
    <PageContainer>
      <CreateManualClPositionPageContent />
    </PageContainer>
  )
}
